import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { categoryService, adminService, productService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import AdminTabs from '../components/AdminTabs'
import DashboardTab from '../components/admin/DashboardTab'
import ProductsTab from '../components/admin/ProductsTab'
import OrdersTab from '../components/admin/OrdersTab'
import UsersTab from '../components/admin/UsersTab'
import SettingsTab from '../components/admin/SettingsTab'
import SliderTab from '../components/admin/SliderTab'
import CitiesTab from '../components/admin/CitiesTab'
import PromoCodesTab from '../components/admin/PromoCodesTab'
import SimpleAttributesTab from '../components/admin/SimpleAttributesTab'
import AttributesTab from '../components/admin/AttributesTab'
import GoogleMerchantUpdater from '../components/admin/GoogleMerchantUpdater'

const Admin = () => {
  const { isAuthenticated, user, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard')
  const [authError, setAuthError] = useState(null)
  const [authResolved, setAuthResolved] = useState(false)
  const [initializing, setInitializing] = useState(true)

  // Give AuthContext time to initialize
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitializing(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  // Wait for authentication to be fully resolved
  useEffect(() => {
    // Don't process until basic initialization is done
    if (initializing) {
      return
    }

    console.log('[ADMIN AUTH] Auth state changed:', {
      loading,
      isAuthenticated,
      userExists: !!user,
      userIsAdmin: user?.isAdmin
    })

    // Don't resolve auth until loading is complete
    if (loading) {
      console.log('[ADMIN AUTH] Still loading...')
      setAuthResolved(false)
      return
    }

    // Check token exists first
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('[ADMIN AUTH] No token found, redirecting to login')
      navigate('/login?returnTo=/admin', { replace: true })
      return
    }

    // If we have a token but no user data and not loading, there might be an auth error
    // Wait a bit more for the auth context to resolve
    if (!isAuthenticated && !user) {
      console.log('[ADMIN AUTH] Token exists but no user data - auth may have failed')
      // Give it a moment for auth context to finish loading
      const authTimeout = setTimeout(() => {
        if (!isAuthenticated && !loading) {
          console.log('[ADMIN AUTH] Auth failed after timeout, redirecting to login')
          navigate('/login?returnTo=/admin', { replace: true })
        }
      }, 1000) // Wait 1 second for auth to resolve
      
      return () => clearTimeout(authTimeout)
    }

    // Check authentication status
    if (!isAuthenticated) {
      console.log('[ADMIN AUTH] Not authenticated, redirecting to login')
      navigate('/login?returnTo=/admin', { replace: true })
      return
    }

    // Check user exists
    if (!user) {
      console.log('[ADMIN AUTH] No user data, redirecting to login')
      navigate('/login?returnTo=/admin', { replace: true })
      return
    }

    // Check admin privileges
    if (!user.isAdmin) {
      console.log('[ADMIN AUTH] User not admin, showing error')
      setAuthError('Access denied. Admin privileges required.')
      setAuthResolved(true)
      return
    }

    // All checks passed
    console.log('[ADMIN AUTH] Authentication successful for admin:', user.email)
    setAuthError(null)
    setAuthResolved(true)

  }, [initializing, loading, isAuthenticated, user, navigate])

  // Update URL when tab changes - ONLY when exactly on /admin route
  // searchParams must NOT be in deps - it changing on navigate would re-trigger this and hijack navigation
  useEffect(() => {
    if (location.pathname !== '/admin') return
    const currentTab = searchParams.get('tab')
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, location.pathname])

  // Navigation helpers
  const navigateToProductForm = (productId = null) => {
    if (productId) {
      navigate(`/admin/products/edit/${productId}`)
    } else {
      navigate('/admin/products/new')
    }
  }

  const navigateToCategoryForm = (categoryId = null) => {
    if (categoryId) {
      navigate(`/admin/categories/edit/${categoryId}`)
    } else {
      navigate('/admin/categories/new')
    }
  }

  // Delete handlers
  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الفئة؟')) {
      try {
        await categoryService.deleteCategory(categoryId)
        toast.success('تم حذف الفئة بنجاح')
        queryClient.invalidateQueries('admin-categories')
      } catch (error) {
        toast.error('فشل في حذف الفئة')
      }
    }
  }

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      try {
        await productService.deleteProduct(productId)
        toast.success('تم حذف المنتج بنجاح')
        queryClient.invalidateQueries(['admin-products-paginated'])
      } catch (error) {
        toast.error('فشل في حذف المنتج')
      }
    }
  }

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => categoryService.getCategories(),
    enabled: isAuthenticated && (activeTab === 'categories' || activeTab === 'dashboard'),
  })

  const { data: adminStats, isLoading: adminStatsLoading, refetch: refetchAdminStats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminService.getStats(),
    enabled: isAuthenticated && activeTab === 'dashboard',
    staleTime: 30000, // Data becomes stale after 30 seconds
    cacheTime: 60000, // Cache for 1 minute
  })

  // Force refresh function for manual updates
  const handleRefreshStats = async () => {
    try {
      await refetchAdminStats()
    } catch (error) {
      console.error('Failed to refresh stats:', error)
    }
  }

  // Loading state - show while authentication is being resolved
  if (initializing || !authResolved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">جاري تحميل لوحة الإدارة...</p>
        </div>
      </div>
    )
  }

  // Show auth error if any
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">غير مصرح</h1>
          <p className="text-gray-600 mb-4">{authError}</p>
          <button
            onClick={() => {
              setAuthError(null)
              navigate('/login', { replace: true })
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            الذهاب لتسجيل الدخول
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        {/* Admin Sidebar */}
        <AdminTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        
        {/* Main Content */}
        <div className="flex-1 max-w-full overflow-hidden">
          <div className="p-2 md:p-8 max-w-full">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <DashboardTab 
                adminStats={adminStats}
                adminStatsLoading={adminStatsLoading}
                onRefreshStats={handleRefreshStats}
              />
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <ProductsTab
                onAddProduct={() => navigateToProductForm()}
                onEditProduct={(productId) => navigateToProductForm(productId)}
                onDeleteProduct={handleDeleteProduct}
              />
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h1 className="text-2xl font-bold text-gray-900">إدارة الفئات</h1>
                  <button
                    onClick={() => navigateToCategoryForm()}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    إضافة فئة جديدة
                  </button>
                </div>

                {categoriesLoading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            الفئة
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                            الوصف
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                            الحالة
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            الإجراءات
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {categories?.flat_categories?.map((category) => (
                          <tr key={category.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  {category.image ? (
                                    <img
                                      className="h-10 w-10 rounded-full object-cover"
                                      src={`/uploads/categories/${category.image}`}
                                      alt={category.name}
                                    />
                                  ) : (
                                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                      <span className="text-gray-600 text-sm">صورة</span>
                                    </div>
                                  )}
                                </div>
                                <div className="mr-4">
                                  <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                  <div className="text-sm text-gray-500">{category.slug}</div>
                                  {/* Mobile-only additional info */}
                                  <div className="md:hidden text-xs text-gray-500 mt-1">
                                    <div>{category.description || 'لا يوجد وصف'}</div>
                                    <div className="mt-1">
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        category.is_active 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {category.is_active ? 'نشط' : 'غير نشط'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 hidden md:table-cell">
                              {category.description || 'لا يوجد وصف'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                category.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {category.is_active ? 'نشط' : 'غير نشط'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex flex-col space-y-1 md:flex-row md:space-y-0 md:space-x-2">
                                <button
                                  onClick={() => navigateToCategoryForm(category.id)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                                  title="Edit Category"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category.id)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                                  title="Delete Category"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <OrdersTab />
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <UsersTab />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SettingsTab />
            )}

            {/* Slider Tab */}
            {activeTab === 'slider' && (
              <SliderTab />
            )}

            {/* Cities Tab */}
            {activeTab === 'cities' && (
              <CitiesTab />
            )}

            {/* Promo Codes Tab */}
            {activeTab === 'promo-codes' && (
              <PromoCodesTab />
            )}

            {/* Google Merchant Center Tab */}
            {activeTab === 'google-merchant' && (
              <GoogleMerchantUpdater />
            )}

            {/* Simple Attributes Tab */}
            {activeTab === 'simple-attributes' && (
              <AttributesTab />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Admin
